import { computed, inject, Inject, Injectable, signal } from '@angular/core';

import { PrismStepper } from '@shure/prism-angular-components';

import { STEPPER_CONFIG } from './provide-step-router';
import { StepConfig, StepperConfig } from './stepper-config';

/**
 * Registers available steps and tracks the currently active step. See README for usage instructions.
 */
@Injectable()
export class StepRouter {
	private stepper?: PrismStepper;
	public readonly steps: StepConfig[];
	public readonly activeStepNumber = signal(0);

	public readonly activeStep = computed(() => {
		const activeStepNumber = this.activeStepNumber();
		return this.steps[activeStepNumber];
	});

	constructor(@Inject(STEPPER_CONFIG) private readonly stepperConfig: StepperConfig) {
		this.steps = stepperConfig.steps.map((step) => inject(step));
	}

	public setStepper(stepper: PrismStepper): void {
		this.stepper = stepper;
	}

	public nextStep(): void {
		if (!this.stepper) {
			throw new Error('You must call setStepper before interacting with the StepperStateService');
		}

		const newStep = this.activeStepNumber() + 1;
		if (newStep >= this.stepperConfig.steps.length) {
			throw new Error(`Invalid step: ${newStep}`);
		}

		this.stepper.next();
		this.activeStepNumber.set(newStep);
	}

	public previousStep(): void {
		if (!this.stepper) {
			throw new Error('You must call setStepper before interacting with the StepperStateService');
		}

		const newStep = this.activeStepNumber() - 1;
		if (newStep >= this.stepperConfig.steps.length) {
			throw new Error(`Invalid step: ${newStep}`);
		}

		this.stepper.previous();
		this.activeStepNumber.set(newStep);
	}
}
